import type { PrintJob } from "./types";

export interface PrinterAdapter {
  print(
    job: PrintJob,
  ): Promise<{ ok: boolean; simulated?: boolean; message: string }>;
  openDrawer(
    job: PrintJob,
  ): Promise<{ ok: boolean; simulated?: boolean; message: string }>;
}

export class SimulatedPrinterAdapter implements PrinterAdapter {
  async print(job: PrintJob) {
    console.info("Ticket simulado", job);
    return {
      ok: true,
      simulated: true,
      message: "Ticket simulado correctamente.",
    };
  }
  async openDrawer(job: PrintJob) {
    console.info("Cajón simulado", job);
    return {
      ok: true,
      simulated: true,
      message: "Apertura de cajón simulada.",
    };
  }
}

declare global {
  interface Window {
    epson?: { ePOSDevice: new () => EposDevice };
  }
}
interface EposPrinter {
  addText(text: string): void;
  addFeedLine(lines: number): void;
  addPulse(drawer: number, time: number): void;
  send(): void;
  onreceive?: (response: { success: boolean }) => void;
  onerror?: (error: unknown) => void;
}
interface EposDevice {
  connect(
    host: string,
    port: number,
    callback: (result: string) => void,
    options?: { eposprint?: boolean },
  ): void;
  createDevice(
    id: string,
    type: string,
    options: { crypto: boolean; buffer: boolean },
    callback: (device: EposPrinter | null, code: string) => void,
  ): void;
}

async function loadEpsonSdk(): Promise<void> {
  if (window.epson) return;
  const source =
    localStorage.getItem("casaHawaEpsonSdkUrl") || "/epson/epos-2.27.0.js";
  await new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = source;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("No se pudo cargar Epson ePOS SDK."));
    document.head.appendChild(script);
  });
}

export function ticketText(payload: unknown): string {
  const wrapper = payload as {
    business?: {
      name?: string;
      businessName?: string;
      address?: string;
      phone?: string;
      footer?: string;
    };
    ticket?: unknown;
    copy?: boolean;
    return?: { id?: string; amount?: number; reason?: string };
    returnLines?: Array<{ name?: string; units?: number; total?: number }>;
    refunds?: Array<{ method?: string; amount?: number }>;
  };
  const value = (wrapper.ticket || wrapper) as {
    sale?: { folio?: string; total?: number };
    lines?: Array<{ name?: string; units?: number; total?: number }>;
    payments?: Array<{ method?: string; amount?: number; change?: number }>;
  };
  const returnInfo = wrapper.return;
  const lines = returnInfo ? wrapper.returnLines || [] : value.lines || [];
  return [
    wrapper.business?.businessName || wrapper.business?.name || "Casa Hawa",
    wrapper.business?.address || "",
    wrapper.business?.phone || "",
    wrapper.copy ? "COPIA" : "",
    value.sale?.folio || "",
    returnInfo ? "DEVOLUCIÓN" : "",
    returnInfo?.reason || "",
    ...lines.map(
      (line) =>
        `${line.units || 0} x ${line.name || "Producto"}  $${Number(line.total || 0).toFixed(2)}`,
    ),
    `${returnInfo ? "REEMBOLSO" : "TOTAL"}  $${Number(returnInfo?.amount ?? value.sale?.total ?? 0).toFixed(2)}`,
    ...(returnInfo
      ? (wrapper.refunds || []).map(
          (refund) =>
            `${refund.method || "Reembolso"}  $${Number(refund.amount || 0).toFixed(2)}`,
        )
      : (value.payments || []).map(
          (payment) =>
            `${payment.method || "Pago"}  $${Number(payment.amount || 0).toFixed(2)}${Number(payment.change || 0) > 0 ? `  Cambio $${Number(payment.change).toFixed(2)}` : ""}`,
        )),
    "Comprobante no fiscal",
    wrapper.business?.footer || "Gracias por tu compra",
    "\n",
  ]
    .filter(Boolean)
    .join("\n");
}

export class EpsonEposPrinterAdapter implements PrinterAdapter {
  constructor(
    private host: string,
    private deviceId = "local_printer",
  ) {}
  private async device(): Promise<EposPrinter> {
    await loadEpsonSdk();
    return new Promise((resolve, reject) => {
      const epos = new window.epson!.ePOSDevice();
      epos.connect(
        this.host,
        8043,
        (result) => {
          if (result !== "OK" && result !== "SSL_CONNECT_OK")
            return reject(new Error(`La impresora respondió ${result}.`));
          epos.createDevice(
            this.deviceId,
            "PRINTER",
            { crypto: true, buffer: false },
            (device, code) =>
              device
                ? resolve(device)
                : reject(new Error(`No se pudo abrir la impresora: ${code}.`)),
          );
        },
        { eposprint: true },
      );
    });
  }
  async print(job: PrintJob) {
    const printer = await this.device();
    printer.addText(ticketText(job.payload));
    printer.addFeedLine(3);
    if (job.openDrawer) printer.addPulse(0, 100);
    return this.send(printer, "Ticket enviado a la impresora.");
  }
  async openDrawer(_job: PrintJob) {
    const printer = await this.device();
    printer.addPulse(0, 100);
    return this.send(printer, "Cajón abierto.");
  }
  private send(
    printer: EposPrinter,
    message: string,
  ): Promise<{ ok: boolean; message: string }> {
    return new Promise((resolve, reject) => {
      printer.onreceive = (response) =>
        response.success
          ? resolve({ ok: true, message })
          : reject(new Error("La impresora rechazó el trabajo."));
      printer.onerror = (error) =>
        reject(
          error instanceof Error ? error : new Error("Falló la impresora."),
        );
      printer.send();
    });
  }
}

export const printerFor = (config: {
  mode: "SIMULATED" | "EPSON";
  host?: string;
  deviceId?: string;
}): PrinterAdapter =>
  config.mode === "EPSON" && config.host
    ? new EpsonEposPrinterAdapter(config.host, config.deviceId)
    : new SimulatedPrinterAdapter();
